/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

// Policy Categories
export enum PolicyCategory {
  DataGovernance = 'DataGovernance',
  Security = 'Security',
  FinancialCompliance = 'FinancialCompliance',
  Operational = 'Operational',
  HumanResources = 'HumanResources',
  Legal = 'Legal',
  RiskManagement = 'RiskManagement',
  Environmental = 'Environmental',
  Ethics = 'Ethics'
}

export enum PolicySubcategory {
  // Data Governance
  DataClassification = 'DataClassification',
  DataRetention = 'DataRetention',
  DataPrivacy = 'DataPrivacy',
  DataSecurity = 'DataSecurity',
  DataQuality = 'DataQuality',

  // Security
  AccessControl = 'AccessControl',
  IncidentResponse = 'IncidentResponse',
  BusinessContinuity = 'BusinessContinuity',
  VulnerabilityManagement = 'VulnerabilityManagement',
  SecurityArchitecture = 'SecurityArchitecture',

  // Financial Compliance
  AML = 'AML',
  KYC = 'KYC',
  Sanctions = 'Sanctions',
  Reporting = 'Reporting',
  RiskManagement = 'RiskManagement',

  // Operational
  ChangeManagement = 'ChangeManagement',
  VendorManagement = 'VendorManagement',
  QualityAssurance = 'QualityAssurance',
  ServiceDelivery = 'ServiceDelivery',
  ResourceManagement = 'ResourceManagement',

  // Human Resources
  EmployeeLifecycle = 'EmployeeLifecycle',
  Compensation = 'Compensation',
  Performance = 'Performance',
  Training = 'Training',
  WorkplaceEnvironment = 'WorkplaceEnvironment'
}

export enum PolicyStatus {
  Draft = 'Draft',
  UnderReview = 'UnderReview',
  Approved = 'Approved',
  Implemented = 'Implemented',
  Enforced = 'Enforced',
  Deprecated = 'Deprecated',
  Archived = 'Archived'
}

export enum PolicyPriority {
  Critical = 'Critical',
  High = 'High',
  Medium = 'Medium',
  Low = 'Low'
}

export enum PolicyImpact {
  Critical = 'Critical',
  High = 'High',
  Medium = 'Medium',
  Low = 'Low',
  Minimal = 'Minimal'
}

// Core Policy Types
export interface Policy {
  id: string;
  name: string;
  description: string;
  category: PolicyCategory;
  subcategory: PolicySubcategory;
  status: PolicyStatus;
  priority: PolicyPriority;

  // Policy Content
  content: PolicyContent;
  requirements: PolicyRequirement[];
  controls: PolicyControl[];

  // Implementation Details
  implementationPlan: ImplementationPlan;
  timeline: PolicyTimeline;
  dependencies: PolicyDependencies;

  // Economic Impact
  costs: PolicyCosts;
  benefits: PolicyBenefits;
  roi: PolicyROI;

  // Risk Assessment
  riskAssessment: PolicyRiskAssessment;

  // Compliance Mapping
  regulatoryFrameworks: string[];
  complianceRequirements: string[];

  // Metadata
  version: string;
  createdBy: string;
  createdAt: number;
  lastUpdated: number;
  approvedBy?: string;
  approvedAt?: number;
  implementedBy?: string;
  implementedAt?: number;

  // Performance Metrics
  effectivenessScore: number;
  complianceScore: number;
  implementationProgress: number;
}

export interface PolicyContent {
  purpose: string;
  scope: string;
  applicability: string[];
  policyStatement: string;
  definitions: Record<string, string>;
  responsibilities: PolicyResponsibility[];
  procedures: PolicyProcedure[];
  exceptions: PolicyException[];
  references: string[];
}

export interface PolicyRequirement {
  id: string;
  description: string;
  category: string;
  mandatory: boolean;
  measurable: boolean;
  targetValue?: number;
  currentvalue?: number;
  dueDate?: number;
}

export interface PolicyControl {
  id: string;
  type: 'Preventive' | 'Detective' | 'Corrective' | 'Compensating';
  description: string;
  implementation: string;
  testing: string;
  frequency: string;
  automationLevel: 'Manual' | 'Semi-Automated' | 'Automated';
}

export interface ImplementationPlan {
  phases: ImplementationPhase[];
  resourcesRequired: ResourceRequirement[];
  systemChanges: SystemChange[];
  trainingRequirements: TrainingRequirement[];
  communicationPlan: CommunicationPlan[];
}

export interface ImplementationPhase {
  id: string;
  name: string;
  description: string;
  duration: number;
  dependencies: string[];
  deliverables: string[];
  resources: string[];
  risks: string[];
}

export interface PolicyTimeline {
  draftDate: number;
  reviewDate: number;
  approvalDate?: number;
  implementationStartDate?: number;
  implementationEndDate?: number;
  enforcementDate?: number;
  reviewDate?: number;
  sunsetDate?: number;
}

export interface PolicyDependencies {
  prerequisites: string[];
  conflicts: string[];
  synergies: string[];
  superseded: string[];
  frameworks: string[];
}

// Economic Impact Types
export interface PolicyCosts {
  oneTimeCosts: {
    developmentCosts: number;
    implementationCosts: number;
    trainingCosts: number;
    technologyCosts: number;
    consultingCosts: number;
  };
  recurringCosts: {
    maintenanceCosts: number;
    monitoringCosts: number;
    trainingCosts: number;
    licensingCosts: number;
    personnelCosts: number;
  };
  opportunityCosts: number;
  totalImplementationCost: number;
  totalAnnualCost: number;
}

export interface PolicyBenefits {
  riskReduction: {
    complianceRiskReduction: number;
    operationalRiskReduction: number;
    financialRiskReduction: number;
    reputationalRiskReduction: number;
  };
  efficiencyGains: {
    processEfficiency: number;
    resourceEfficiency: number;
    costSavings: number;
    productivityIncrease: number;
  };
  revenueOpportunities: {
    newMarkets: number;
    competitiveAdvantage: number;
    customerTrust: number;
    brandValue: number;
  };
  totalQuantifiedBenefit: number;
}

export interface PolicyROI {
  paybackPeriod: number; // in months
  netPresentValue: number;
  internalRateOfReturn: number;
  benefitCostRatio: number;
  riskAdjustedReturn: number;
  confidenceLevel: number;
}

// Risk Assessment Types
export interface PolicyRiskAssessment {
  implementationRisks: AssessedRisk[];
  operationalRisks: AssessedRisk[];
  complianceRisks: AssessedRisk[];
  financialRisks: AssessedRisk[];
  overallRiskScore: number;
  riskMitigationStrategies: RiskMitigationStrategy[];
}

export interface AssessedRisk {
  id: string;
  description: string;
  category: string;
  probability: number; // 0-100
  impact: number; // 0-100
  riskScore: number;
  mitigationStrategy: string;
  residualRisk: number;
}

export interface RiskMitigationStrategy {
  riskId: string;
  strategy: 'Accept' | 'Avoid' | 'Transfer' | 'Mitigate';
  actions: string[];
  resources: string[];
  timeline: number;
  effectiveness: number;
}

// Resource Management Types
export interface ResourceRequirement {
  id: string;
  type: 'Personnel' | 'Technology' | 'Infrastructure' | 'External';
  name: string;
  description: string;
  quantity: number;
  unit: string;
  cost: number;
  availability: number;
  allocation: string[];
}

export interface SystemChange {
  id: string;
  system: string;
  changeType: 'Configuration' | 'Development' | 'Integration' | 'Upgrade';
  description: string;
  complexity: 'Low' | 'Medium' | 'High';
  estimatedEffort: number;
  dependencies: string[];
  risks: string[];
}

export interface TrainingRequirement {
  id: string;
  targetAudience: string[];
  trainingType: 'Mandatory' | 'Recommended' | 'Optional';
  deliveryMethod: 'Classroom' | 'Online' | 'On-the-job' | 'Workshop';
  duration: number;
  frequency: string;
  materials: string[];
  assessmentRequired: boolean;
}

export interface CommunicationPlan {
  id: string;
  audience: string[];
  message: string;
  channel: 'Email' | 'Meeting' | 'Intranet' | 'Newsletter' | 'Workshop';
  timing: string;
  frequency: string;
  owner: string;
}

// Policy Responsibility Types
export interface PolicyResponsibility {
  role: string;
  department: string;
  responsibilities: string[];
  authorities: string[];
  accountabilities: string[];
}

export interface PolicyProcedure {
  id: string;
  name: string;
  description: string;
  steps: ProcedureStep[];
  inputs: string[];
  outputs: string[];
  tools: string[];
  frequency: string;
}

export interface ProcedureStep {
  stepNumber: number;
  description: string;
  responsibleParty: string;
  tools: string[];
  inputs: string[];
  outputs: string[];
  timeRequired: number;
  dependencies: number[];
}

export interface PolicyException {
  id: string;
  description: string;
  justification: string;
  conditions: string[];
  approvalRequired: string[];
  reviewDate?: number;
  expiryDate?: number;
  riskMitigation: string;
}

// Policy Management Types
export interface PolicyTemplate {
  id: string;
  name: string;
  description: string;
  category: PolicyCategory;
  subcategory: PolicySubcategory;
  template: PolicyContent;
  defaultControls: PolicyControl[];
  commonRequirements: PolicyRequirement[];
  estimatedImplementationTime: number;
  estimatedCost: PolicyCosts;
 适用性: string[];
}

export interface PolicyWorkflow {
  id: string;
  name: string;
  stages: WorkflowStage[];
  approvers: WorkflowApprover[];
  notifications: WorkflowNotification[];
  escalations: WorkflowEscalation[];
}

export interface WorkflowStage {
  id: string;
  name: string;
  description: string;
  requiredActions: string[];
  approvers: string[];
  timeLimit: number;
  parallelProcessing: boolean;
}

export interface WorkflowApprover {
  id: string;
  name: string;
  role: string;
  department: string;
  approvalType: 'Required' | 'Optional' | 'Informational';
  sequence: number;
}

export interface WorkflowNotification {
  trigger: string;
  recipients: string[];
  template: string;
  channel: string;
  timing: 'Immediate' | 'Daily' | 'Weekly';
}

export interface WorkflowEscalation {
  condition: string;
  escalatedTo: string[];
  delay: number;
  action: string;
}

// Policy Violation and Enforcement Types
export interface PolicyViolation {
  id: string;
  policyId: string;
  policyName: string;
  violationType: 'NonCompliance' | 'PartialCompliance' | 'ProcessDeviation' | 'DocumentationIssue';
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  description: string;
  detectedBy: string;
  detectionDate: number;
  location: string;
  impactedSystems: string[];
  rootCauseAnalysis: string;
  immediateActions: string[];
  longTermRemediation: string[];
  responsibleParty: string;
  status: 'Open' | 'InProgress' | 'Resolved' | 'Closed';
  resolutionDate?: number;
  financialImpact: number;
  repeatViolation: boolean;
}

export interface EnforcementAction {
  id: string;
  violationId: string;
  actionType: 'Warning' | 'CorrectiveAction' | 'Penalty' | 'Suspension' | 'Termination';
  description: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  issuedBy: string;
  issuedDate: number;
  deadline: number;
  status: 'Issued' | 'Acknowledged' | 'InProgress' | 'Completed' | 'Overdue';
  cost: number;
  consequences: string[];
}

// Policy Performance Metrics
export interface PolicyMetrics {
  policyId: string;
  complianceRate: number;
  effectivenessScore: number;
  implementationProgress: number;
  userSatisfaction: number;
  auditFindings: number;
  violationsCount: number;
  costEffectiveness: number;
  lastUpdated: number;
  trends: {
    complianceTrend: number[];
    effectivenessTrend: number[];
    violationTrend: number[];
  };
}

// Policy Assessment Types
export interface PolicyAssessment {
  id: string;
  policyId: string;
  assessmentType: 'Compliance' | 'Effectiveness' | 'Efficiency' | 'Risk';
  methodology: string;
  scope: string;
  criteria: AssessmentCriteria[];
  findings: AssessmentFinding[];
  recommendations: AssessmentRecommendation[];
  overallScore: number;
  assessor: string;
  assessmentDate: number;
  nextAssessmentDate: number;
}

export interface AssessmentCriteria {
  id: string;
  description: string;
  weight: number;
  score: number;
  maxScore: number;
  evidence: string[];
  comments: string;
}

export interface AssessmentFinding {
  id: string;
  category: 'Strength' | 'Weakness' | 'Opportunity' | 'Threat';
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  description: string;
  impact: string;
  evidence: string[];
  recommendationId?: string;
}

export interface AssessmentRecommendation {
  id: string;
  description: string;
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  effort: 'High' | 'Medium' | 'Low';
  impact: 'High' | 'Medium' | 'Low';
  timeline: number;
  resources: string[];
  responsibleParty: string;
  status: 'Open' | 'InProgress' | 'Completed';
}

// Game Integration Types
export interface PolicyGameEffect {
  policyId: string;
  effects: {
    compliance: number;
    threat: number;
    budget: number;
    efficiency: number;
    productivity: number;
    customerSatisfaction: number;
    employeeMorale: number;
  };
  duration: number;
  conditions: GameCondition[];
}

export interface GameCondition {
  type: 'Building' | 'Resource' | 'Metric' | 'Time';
  parameter: string;
  operator: 'equals' | 'greaterThan' | 'lessThan' | 'between';
  value: any;
}

export interface PolicyMilestone {
  id: string;
  policyId: string;
  name: string;
  description: string;
  trigger: string;
  reward: {
    type: 'Budget' | 'Compliance' | 'Building' | 'Research';
    value: number;
  };
  unlocked: boolean;
  completedDate?: number;
}

export interface PolicyChallenge {
  id: string;
  name: string;
  description: string;
  difficulty: 'Easy' | 'Medium' | 'Hard' | 'Expert';
  objectives: ChallengeObjective[];
  timeLimit?: number;
  rewards: ChallengeReward[];
  requirements: ChallengeRequirement[];
  isActive: boolean;
}

export interface ChallengeObjective {
  id: string;
  description: string;
  metric: string;
  target: number;
  operator: 'equals' | 'greaterThan' | 'lessThan';
  weight: number;
  achieved: boolean;
}

export interface ChallengeReward {
  type: 'Budget' | 'Compliance' | 'Research' | 'Building' | 'Policy';
  value: number | string;
  description: string;
}

export interface ChallengeRequirement {
  type: 'Building' | 'Policy' | 'Research' | 'Metric';
  value: string | number;
  description: string;
}

// Policy AI and Analytics Types
export interface PolicyInsight {
  id: string;
  type: 'Recommendation' | 'Anomaly' | 'Opportunity' | 'Risk';
  title: string;
  description: string;
  confidence: number;
  impact: 'High' | 'Medium' | 'Low';
  effort: 'High' | 'Medium' | 'Low';
  actions: string[];
  data: any;
  timestamp: number;
}

export interface PolicyAnalytics {
  policyId: string;
  metrics: {
    adoptionRate: number;
    effectivenessScore: number;
    costPerCompliancePoint: number;
    userEngagement: number;
    processImprovement: number;
  };
  trends: {
    complianceTrend: number[];
    costTrend: number[];
    effectivenessTrend: number[];
    violationTrend: number[];
  };
  benchmarks: {
    industryAverage: number;
    bestInClass: number;
    peerComparison: number;
  };
  predictions: {
    nextPeriodCompliance: number;
    riskScore: number;
    requiredInvestment: number;
    roiProjection: number;
  };
}

// Export all types for easy importing
export type {
  Policy,
  PolicyContent,
  PolicyRequirement,
  PolicyControl,
  ImplementationPlan,
  PolicyTimeline,
  PolicyDependencies,
  PolicyCosts,
  PolicyBenefits,
  PolicyROI,
  PolicyRiskAssessment,
  AssessedRisk,
  RiskMitigationStrategy,
  ResourceRequirement,
  SystemChange,
  TrainingRequirement,
  CommunicationPlan,
  PolicyResponsibility,
  PolicyProcedure,
  ProcedureStep,
  PolicyException,
  PolicyTemplate,
  PolicyWorkflow,
  WorkflowStage,
  WorkflowApprover,
  WorkflowNotification,
  WorkflowEscalation,
  PolicyViolation,
  EnforcementAction,
  PolicyMetrics,
  PolicyAssessment,
  AssessmentCriteria,
  AssessmentFinding,
  AssessmentRecommendation,
  PolicyGameEffect,
  GameCondition,
  PolicyMilestone,
  PolicyChallenge,
  ChallengeObjective,
  ChallengeReward,
  ChallengeRequirement,
  PolicyInsight,
  PolicyAnalytics
};